import { qfRestGet } from './qfClient';

export const DEALER_GEX_HEATMAP_PATH = '/v1/steamroom/dealer-gex-heatmap';

export const HEATMAP_HORIZON_DAYS = 33;
export const HEATMAP_HORIZON_MAX = 90;
export const HEATMAP_HORIZON_OPTIONS = [7, 14, 21, 33, 45, 60, 90] as const;
export type HeatmapHorizon = (typeof HEATMAP_HORIZON_OPTIONS)[number];
export const HEATMAP_STALE_MS = 5 * 60 * 1000;

export function parseHorizonDays(value: unknown): HeatmapHorizon {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n) || n < 1 || n > HEATMAP_HORIZON_MAX) {
    return HEATMAP_HORIZON_DAYS;
  }
  let best: HeatmapHorizon = HEATMAP_HORIZON_DAYS;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const opt of HEATMAP_HORIZON_OPTIONS) {
    const d = Math.abs(opt - n);
    if (d < bestDist) {
      best = opt;
      bestDist = d;
    }
  }
  return best;
}

export type HeatmapSize = 'oi' | 'vol_inf';
export type HeatmapGreek = 'gamma' | 'charm';
export type HeatmapGrid = 'price';
export type EarningsTime = 'bmo' | 'amc' | 'dmh';

export interface HeatmapEarnings {
  date: string;
  time: EarningsTime | null;
}

export interface HeatmapStats {
  net_gex: number;
  peak_abs_date: string | null;
  peak_abs_strike: number | null;
  net_charm: number;
  charm_peak_date: string | null;
  charm_peak_strike: number | null;
}

/** One cash session of dealer-signed ES gamma per point. Positive is dealer long. */
export interface HeatmapTerrain {
  date: string;
  times: string[];
  strikes: number[];
  z: number[][];
  plus2: number;
  plus1: number;
  minus1: number;
  minus2: number;
  path: (number | null)[];
  asof: string | null;
}

export interface DealerGexHeatmap {
  symbol: string;
  date: string;
  spot: number;
  size: HeatmapSize;
  units: '$gamma/1%';
  calendarization: 'remaining';
  horizon_days: number;
  view: 'dealer';
  grid: HeatmapGrid;
  dates: string[];
  strikes: number[];
  z: number[][];
  charm: number[][];
  cone_upper: number[];
  cone_lower: number[];
  stats: HeatmapStats;
  earnings: HeatmapEarnings | null;
  terrain: HeatmapTerrain | null;
}

export interface FetchHeatmapParams {
  symbol: string;
  size: HeatmapSize;
  horizonDays?: number;
  date?: string;
}

const EMPTY_STATS: HeatmapStats = {
  net_gex: 0,
  peak_abs_date: null,
  peak_abs_strike: null,
  net_charm: 0,
  charm_peak_date: null,
  charm_peak_strike: null,
};

export function emptyHeatmap(
  partial: Pick<DealerGexHeatmap, 'symbol' | 'size'> & { date?: string }
): DealerGexHeatmap {
  return {
    symbol: partial.symbol,
    date: partial.date ?? '',
    spot: 0,
    size: partial.size,
    units: '$gamma/1%',
    calendarization: 'remaining',
    horizon_days: HEATMAP_HORIZON_DAYS,
    view: 'dealer',
    grid: 'price',
    dates: [],
    strikes: [],
    z: [],
    charm: [],
    cone_upper: [],
    cone_lower: [],
    stats: { ...EMPTY_STATS },
    earnings: null,
    terrain: null,
  };
}

export function isEmptyHeatmap(data: DealerGexHeatmap | null | undefined): boolean {
  if (!data) return true;
  return data.dates.length === 0 || data.strikes.length === 0 || data.z.length === 0;
}

function asString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function asFinite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function parseSize(value: unknown): HeatmapSize | null {
  return value === 'oi' || value === 'vol_inf' ? value : null;
}

function parseEarningsTime(value: unknown): EarningsTime | null {
  return value === 'bmo' || value === 'amc' || value === 'dmh' ? value : null;
}

function parseEarnings(value: unknown): HeatmapEarnings | null {
  if (value == null) return null;
  if (typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const date = asString(row.date);
  if (!date) return null;
  return { date, time: parseEarningsTime(row.time) };
}

function parseSeries(value: unknown, n: number): number[] {
  if (!Array.isArray(value) || value.length !== n || n === 0) return [];
  const out: number[] = [];
  for (let i = 0; i < n; i++) {
    const cell = asFinite(value[i]);
    if (cell == null) return [];
    out.push(cell);
  }
  return out;
}

function parseZ(value: unknown, nStrikes: number, nDates: number): number[][] {
  if (!Array.isArray(value) || nStrikes === 0 || nDates === 0) return [];
  const rows: number[][] = [];
  for (let i = 0; i < nStrikes; i++) {
    const src = value[i];
    const row: number[] = [];
    for (let j = 0; j < nDates; j++) {
      const cell = Array.isArray(src) ? asFinite(src[j]) : null;
      row.push(cell ?? 0);
    }
    rows.push(row);
  }
  return rows;
}

function parseTerrain(value: unknown): HeatmapTerrain | null {
  if (value == null || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const times = Array.isArray(row.times)
    ? row.times.filter((cell): cell is string => typeof cell === 'string' && cell.length > 0)
    : [];
  const strikeSrc = Array.isArray(row.strikes) ? row.strikes : [];
  const strikes = strikeSrc.map((cell) => asFinite(cell)).filter((cell): cell is number => cell != null);
  if (times.length < 2 || strikes.length < 2 || strikes.length !== strikeSrc.length) return null;
  const z = parseZ(row.z, strikes.length, times.length);
  if (z.length !== strikes.length) return null;
  const plus2 = asFinite(row.plus2);
  const plus1 = asFinite(row.plus1);
  const minus1 = asFinite(row.minus1);
  const minus2 = asFinite(row.minus2);
  const date = asString(row.date);
  if (plus2 == null || plus1 == null || minus1 == null || minus2 == null || date == null) return null;
  if (!Array.isArray(row.path) || row.path.length !== times.length) return null;
  const path: (number | null)[] = [];
  for (const cell of row.path) {
    if (cell == null) {
      path.push(null);
      continue;
    }
    const px = asFinite(cell);
    if (px == null) return null;
    path.push(px);
  }
  const asof = row.asof == null ? null : asString(row.asof);
  if (row.asof != null && asof == null) return null;
  return { date, times, strikes, z, plus2, plus1, minus1, minus2, path, asof };
}

function parseStats(value: unknown): HeatmapStats {
  if (value == null || typeof value !== 'object') return { ...EMPTY_STATS };
  const row = value as Record<string, unknown>;
  return {
    net_gex: asFinite(row.net_gex) ?? 0,
    peak_abs_date: asString(row.peak_abs_date),
    peak_abs_strike: asFinite(row.peak_abs_strike),
    net_charm: asFinite(row.net_charm) ?? 0,
    charm_peak_date: asString(row.charm_peak_date),
    charm_peak_strike: asFinite(row.charm_peak_strike),
  };
}

export function parseDealerGexHeatmap(
  raw: unknown,
  fallback: Pick<DealerGexHeatmap, 'symbol' | 'size'>
): DealerGexHeatmap {
  if (raw == null || typeof raw !== 'object') {
    return emptyHeatmap(fallback);
  }
  const body = raw as Record<string, unknown>;
  const size = parseSize(body.size) ?? fallback.size;
  const dates = Array.isArray(body.dates)
    ? body.dates.filter((d): d is string => typeof d === 'string' && d.length > 0)
    : [];
  const strikes = Array.isArray(body.strikes)
    ? body.strikes.map((k) => asFinite(k)).filter((k): k is number => k != null)
    : [];
  const z = parseZ(body.z, strikes.length, dates.length);
  if (dates.length === 0 || strikes.length === 0 || z.length === 0) {
    return emptyHeatmap({
      symbol: asString(body.symbol) ?? fallback.symbol,
      size,
      date: asString(body.date) ?? '',
    });
  }
  return {
    symbol: asString(body.symbol) ?? fallback.symbol,
    date: asString(body.date) ?? '',
    spot: asFinite(body.spot) ?? 0,
    size,
    units: '$gamma/1%',
    calendarization: 'remaining',
    horizon_days: asFinite(body.horizon_days) ?? HEATMAP_HORIZON_DAYS,
    view: 'dealer',
    grid: 'price',
    dates,
    strikes,
    z,
    charm:
      Array.isArray(body.charm) && body.charm.length === strikes.length
        ? parseZ(body.charm, strikes.length, dates.length)
        : [],
    cone_upper: parseSeries(body.cone_upper, dates.length),
    cone_lower: parseSeries(body.cone_lower, dates.length),
    stats: parseStats(body.stats),
    earnings: parseEarnings(body.earnings),
    terrain: parseTerrain(body.terrain),
  };
}

export async function fetchDealerGexHeatmap(
  params: FetchHeatmapParams
): Promise<DealerGexHeatmap> {
  const query: Record<string, string> = {
    symbol: params.symbol,
    size: params.size,
    horizon_days: String(parseHorizonDays(params.horizonDays ?? HEATMAP_HORIZON_DAYS)),
    calendarization: 'remaining',
  };
  if (params.date) query.date = params.date;
  const body = await qfRestGet<unknown>(DEALER_GEX_HEATMAP_PATH, query);
  return parseDealerGexHeatmap(body, { symbol: params.symbol, size: params.size });
}

export function formatDealerGex(dollars: number): string {
  const abs = Math.abs(dollars);
  const sign = dollars < 0 ? '−' : dollars > 0 ? '+' : '';
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(1)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(1)}K`;
  return `${sign}${abs.toFixed(0)}`;
}

export function earningsCaption(earnings: HeatmapEarnings): string {
  if (earnings.time === 'amc') return 'E AMC';
  if (earnings.time === 'bmo') return 'E BMO';
  if (earnings.time === 'dmh') return 'E DMH';
  return 'E';
}

export function formatMd(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];
  const month = months[Number(m[2]) - 1];
  return month ? `${month} ${Number(m[3])}` : iso;
}

export function heatmapStatusCode(err: unknown): number | null {
  if (!(err instanceof Error)) return null;
  const m = /-> (\d{3})$/.exec(err.message);
  return m ? Number(m[1]) : null;
}
